// Vendor
import { createSelector } from '@ngrx/store';
// Selectors
import { getCalcInfo } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/calcs/calcs.selectors';
import { getChartState } from 'ChartApp/ts/features/fund-chart-page-shared/state/fund-chart-base.selectors';
import { getSecuritiesInfo } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/securities/securities.selectors';
// Constants / utils
import { LINE_ANNOTATION_FORCE_ORIGINAL, LINE_ANNOTATION_FORCE_SINGLE_PANEL, LINE_ANNOTATION_BOOL_KEYS, LINE_ANNOTATION_LINE_COLOR, LINE_ANNOTATION_LINE_WEIGHT, LINE_WEIGHT_LABEL_MAP, } from 'ChartApp/ts/features/fund-chart-page-shared/components/fund-chart-correlations/fund-chart-correlations.constants';
import { INDICATOR, SECURITYLIST } from 'NewAngular/constants/core.constants';
import { getSecurityTypeFromId } from 'NewAngular/utils/security-utils';
// Interfaces / enums
import { CorrelationType } from 'ChartApp/ts/enums/fund-chart-common.enum';
const getLineAnnotations = createSelector(getChartState, (chartState) => chartState.lineAnnotations);
export const getLineAnnotationModalStateForSeries = (securityId, calcId) => createSelector(getLineAnnotations, (lineAnnotations) => {
    const lineAnnotationModalState = {};
    // If the line annotation object does not exist,
    // create the state with default options
    if (!lineAnnotations[securityId] || !lineAnnotations[securityId][calcId]) {
        LINE_ANNOTATION_BOOL_KEYS.forEach((key) => {
            // For Correlation items, the following line annotation options default to `true`
            lineAnnotationModalState[key] =
                [LINE_ANNOTATION_FORCE_ORIGINAL, LINE_ANNOTATION_FORCE_SINGLE_PANEL].includes(key) &&
                    calcId === CorrelationType.CORRELATION;
        });
        return lineAnnotationModalState;
    }
    // If the object does exist, use it to populate the state
    LINE_ANNOTATION_BOOL_KEYS.forEach((key) => {
        if (!lineAnnotationModalState[key]) {
            let val = lineAnnotations[securityId][calcId][key];
            if (val === null || val === undefined) {
                // For Correlation items, the following line annotation options sub in `true` for
                // `null` or `undefined` values
                val =
                    [LINE_ANNOTATION_FORCE_ORIGINAL, LINE_ANNOTATION_FORCE_SINGLE_PANEL].includes(key) &&
                        calcId === CorrelationType.CORRELATION;
            }
            lineAnnotationModalState[key] = val;
        }
    });
    // Add line annotations for line color and line weight
    if (lineAnnotations[securityId][calcId][LINE_ANNOTATION_LINE_COLOR]) {
        lineAnnotationModalState[LINE_ANNOTATION_LINE_COLOR] =
            lineAnnotations[securityId][calcId][LINE_ANNOTATION_LINE_COLOR];
    }
    if (lineAnnotations[securityId][calcId][LINE_ANNOTATION_LINE_WEIGHT]) {
        lineAnnotationModalState[LINE_ANNOTATION_LINE_WEIGHT] =
            lineAnnotations[securityId][calcId][LINE_ANNOTATION_LINE_WEIGHT];
    }
    return lineAnnotationModalState;
});
export const getChartData = createSelector(getChartState, (chartState) => chartState.chartData);
const getChartDataSeries = createSelector(getChartData, (chartData) => chartData.chart_data);
const getEventsSummary = (lineAnnotation) => {
    const events = [];
    if (lineAnnotation.earnings_results) {
        events.push('(E)');
    }
    if (lineAnnotation.dividends) {
        events.push('(D)');
    }
    if (lineAnnotation.splits) {
        events.push('(S)');
    }
    if (lineAnnotation.spinoffs) {
        events.push('(S)');
    }
    if (events.length > 0) {
        return `Events: ${events.join(' ')}`;
    }
    return null;
};
const getLinesSummary = (lineAnnotation) => {
    const lines = [];
    if (lineAnnotation.min) {
        lines.push('Min.');
    }
    if (lineAnnotation.max) {
        lines.push('Max.');
    }
    if (lineAnnotation.avg) {
        lines.push('Avg.');
    }
    if (lines.length > 0) {
        return `Lines: ${lines.join(' ')}`;
    }
    return null;
};
const getLineAnnotationSummaryLines = (lineAnnotationsState, lineAnnotationKey, calcId) => {
    /*
    In this function, we get line annotations Summary as a list of strings.
    Together the strings in the list will describe which line annotations are active.
    For example: ['Line Style: Medium', 'Events: (E) (S)'].
     */
    if (!lineAnnotationsState[lineAnnotationKey] || !lineAnnotationsState[lineAnnotationKey][calcId]) {
        // For correlation items, if no line annotations have been added, the only summary lines needed are
        // 'force original' and 'force_on_single_panel', since they default to `true`
        return calcId === CorrelationType.CORRELATION ? ['Original Data Format: ON', 'Force On Single Panel: ON'] : [];
    }
    const lineAnnotations = lineAnnotationsState[lineAnnotationKey][calcId];
    const summaryList = [];
    const events = getEventsSummary(lineAnnotations);
    const lines = getLinesSummary(lineAnnotations);
    let lineThickness;
    const currentLineWeight = lineAnnotations[LINE_ANNOTATION_LINE_WEIGHT];
    if (currentLineWeight) {
        lineThickness = `Line Thickness: ${LINE_WEIGHT_LABEL_MAP[lineAnnotations[LINE_ANNOTATION_LINE_WEIGHT]]}`;
    }
    const lineCustomizations = [];
    if (lineAnnotations.area_chart) {
        lineCustomizations.push('Area Chart: ON');
    }
    if (calcId === CorrelationType.CORRELATION) {
        if (lineAnnotations.force_original !== false) {
            lineCustomizations.push('Original Data Format: ON');
        }
        if (lineAnnotations.force_on_single_panel !== false) {
            lineCustomizations.push('Force on Single Panel: ON');
        }
    }
    else {
        if (lineAnnotations.force_original) {
            lineCustomizations.push('Original Data Format: ON');
        }
        if (lineAnnotations.force_on_single_panel) {
            lineCustomizations.push('Force on Single Panel: ON');
        }
    }
    if (lineThickness) {
        summaryList.push(lineThickness);
    }
    if (events) {
        summaryList.push(events);
    }
    if (lines) {
        summaryList.push(lines);
    }
    if (lineCustomizations.length > 0) {
        summaryList.push(...lineCustomizations);
    }
    return summaryList;
};
export const getSeriesLineAnnotations = createSelector(getChartDataSeries, getSecuritiesInfo, getCalcInfo, getLineAnnotations, (chartDataSeries, securitiesInfo, calcInfo, lineAnnotations) => {
    if (!chartDataSeries?.length)
        return [];
    return chartDataSeries.reduce((acc, chartDataRow) => {
        const lineAnnotationOptions = chartDataRow.reduce((innerAcc, chartDataSeriesObject) => {
            let securityType;
            const securityId = chartDataSeriesObject.object_id;
            const securityDetailedType = chartDataSeriesObject.object_detailed_type;
            if (chartDataSeriesObject.object_type === CorrelationType.CORRELATION) {
                // Use 'correlation' as type for correlations
                securityType = CorrelationType.CORRELATION;
            }
            else if (chartDataSeriesObject.securitylist_name) {
                // Use 'securitylist' as type for securitylists
                securityType = SECURITYLIST;
            }
            else {
                securityType = getSecurityTypeFromId(chartDataSeriesObject.object_id);
            }
            // Use 'indicator' for calcId if securityType is indicator and 'correlation' if it is correlation
            const calcId = [CorrelationType.CORRELATION, INDICATOR].includes(securityType)
                ? securityType
                : chartDataSeriesObject.object_calc;
            let lineAnnotationKey;
            let label;
            switch (securityType) {
                // For security lists, lineAnnotationKey is securitylist_name.
                // For all other securities, it is the securityId.
                case CorrelationType.CORRELATION:
                    lineAnnotationKey = securityId;
                    label = chartDataSeriesObject.short_label;
                    break;
                case INDICATOR:
                    if (!securitiesInfo[securityId])
                        return '';
                    lineAnnotationKey = securityId;
                    label = securitiesInfo[securityId].security_name;
                    break;
                case SECURITYLIST:
                    if (!calcInfo[calcId])
                        return '';
                    lineAnnotationKey = chartDataSeriesObject.securitylist_name;
                    label = `Securitylist - ${calcInfo[calcId].label}`;
                    break;
                default:
                    if (!calcInfo[calcId] || !securitiesInfo[securityId])
                        return '';
                    lineAnnotationKey = securityId;
                    label = `${securitiesInfo[securityId].security_name} - ${calcInfo[calcId].label}`;
                    break;
            }
            // Line Annotations Summary
            const lineAnnotationsSummaryLines = getLineAnnotationSummaryLines(lineAnnotations, lineAnnotationKey, calcId);
            return [
                ...innerAcc,
                {
                    lineAnnotationsSummaryLines,
                    calcId,
                    label,
                    securityId,
                    securityType,
                    securityDetailedType,
                    color: chartDataSeriesObject.line_format.color,
                    objectType: chartDataSeriesObject.object_type,
                    securitylistName: chartDataSeriesObject.securitylist_name,
                    weight: chartDataSeriesObject.line_format.weight,
                },
            ];
        }, []);
        return [...acc, ...lineAnnotationOptions];
    }, []);
});
